import { defineStore } from 'pinia'
import type { requiredUrls } from './types'
import type { Application } from '@/types.ts'
import { ref, computed } from 'vue'
import { useCompanies } from '@/plugins/companySelection/companies'
import { AppModule, moduleOrder, moduleConfigurations } from '@/core/types'
import { auth } from '@/plugins'

export const useSettingsStore = defineStore('settings', () => {
  const backendUrl = ref<string>('')
  const showContextualMenu = ref(false)
  const apps = ref<Application[]>([])
  const urls = ref<requiredUrls>({
    poweredByUrl: '',
    helpUrl: '',
    newsletterUrl: '',
    changePasswordUrl: '',
    manageUserDataUrl: '',
    productFruitKey: ''
  })

  const { companies: availableCompanies, selectedCompany } = useCompanies()

  const filteredApps = computed(() => {
    if (!selectedCompany.value) {
      return apps.value
    }

    return apps.value.filter((app) => {
      const isForAllCompanies = !app.visibleForCompanies || app.visibleForCompanies.length === 0
      if (isForAllCompanies) {
        return true
      }
      return selectedCompany.value
        ? (app.visibleForCompanies?.includes(selectedCompany.value.id) ?? false)
        : false
    })
  })

  const visibleApps = computed(() => {
    const hasCompanies = availableCompanies.value.length > 0

    return filteredApps.value.filter((app) => {
      const isSimaDesktopInNonSimaModule = app.isSimaDesktop && app.module !== AppModule.SIMA
      if (isSimaDesktopInNonSimaModule && !hasCompanies) {
        return false
      }
      return true
    })
  })

  const appsByType = computed(() => ({
    principal: visibleApps.value.filter((app) => app.type !== 'secondary'),
    secondary: visibleApps.value.filter((app) => app.type === 'secondary')
  }))

  const groupedApps = computed(() => {
    const groups: { [key in AppModule]?: Application[] } = {}

    const appsWithModule = visibleApps.value.filter((app) => app.module)

    for (const app of appsWithModule) {
      const moduleKey = app.module as AppModule
      groups[moduleKey] ??= []
      groups[moduleKey].push(app)
    }

    return moduleOrder
      .map((moduleName) => ({
        name: moduleName,
        config: moduleConfigurations[moduleName],
        apps: groups[moduleName] || []
      }))
      .filter((group) => group.apps.length > 0)
  })

  async function getApplications() {
    const authClient = auth.useAuthentication()
    const accessToken = authClient.getAccessToken()
    const headers = {
      'Content-type': 'application/json; charset=UTF-8',
      'Authorization': `Bearer ${accessToken}`
    }
    const request = await fetch('/applications', { headers })
    apps.value = await request.json()
  }

  return {
    backendUrl,
    showContextualMenu,
    apps,
    urls,
    filteredApps,
    visibleApps,
    appsByType,
    groupedApps,
    getApplications
  }
})
